import { Delete as DeleteIcon } from '@mui/icons-material';
import { Button, IconButton, Paper, Typography } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import { filter, find, map } from 'lodash';
import React, { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikNumericField,
  FormikTextField,
  HelpTooltip,
  PaymentTypeSelect,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { FormField, FormFieldOverride, PaymentType } from 'types';

import { HiddenField } from './HiddenField';
import { Values } from '../ManageShop';

type Props = {
  paymentTypes: PaymentType[];
  hideTitle?: boolean;
  disabled?: boolean;
};

export const CustomerFieldsPayin: React.FC<Props> = ({
  paymentTypes,
  hideTitle,
  disabled,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const formik = useFormikContext<Values>();

  const handlePaymentTypeSelectChange = useCallback(
    (value: string, fieldName: string) => {
      const customerFields = find(paymentTypes, {
        id: value,
      })?.customerFields;

      formik.setFieldValue(
        fieldName,
        map<FormField, FormFieldOverride>(customerFields, (field) => ({
          name: field.name,
          hidden: field.hidden ?? true,
          pattern: field.pattern,
          patternExample: field.patternExample,
          maxLength: field.maxLength,
        })),
      );
    },
    [formik, paymentTypes],
  );

  const getFieldByName = useCallback(
    (paymentTypeId: string, fieldName: string) => {
      const paymentType = find(paymentTypes, { id: paymentTypeId });
      return find(paymentType?.customerFields, { name: fieldName });
    },
    [paymentTypes],
  );

  const getFieldNameLabel = useCallback(
    (fieldName: string, field?: FormField) => {
      if (!field) {
        return fieldName;
      }
      return `${tCommon(`order_form_fields.${field.type}`, {
        defaultValue: field.type,
      })} (${field.name})`;
    },
    [tCommon],
  );

  return (
    <Fragment>
      {!hideTitle && (
        <Typography variant="subtitle1">
          {t('sections.customer_fields_payin.title')}
          <HelpTooltip
            title={t('sections.customer_fields_payin.description')}
          />
        </Typography>
      )}

      <FieldArray
        name="configs"
        render={(arrayHelpers) => (
          <FormControls>
            {map(formik.values.configs, (config, i) => {
              const existing = map(
                formik.values.configs,
                (c) => c.paymentTypeId,
              );
              const availablePaymentTypes = filter(
                paymentTypes,
                (t: PaymentType) =>
                  !existing.includes(t.id) || config.paymentTypeId === t.id,
              );
              return (
                <Paper sx={{ px: 4, py: 6 }} key={i}>
                  <FormControls>
                    <div className="tw-flex tw-items-start">
                      <PaymentTypeSelect
                        sx={{ mr: 1, flex: 1 }}
                        name={`configs[${i}].paymentTypeId`}
                        paymentTypes={availablePaymentTypes}
                        onChange={(value) =>
                          handlePaymentTypeSelectChange(
                            value,
                            `configs[${i}].customerFields`,
                          )
                        }
                        noneOption
                        disabled={disabled}
                      />
                      {!disabled && (
                        <IconButton onClick={() => arrayHelpers.remove(i)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      )}
                    </div>
                    <div>
                      <FieldArray name={`configs[${i}].customerFields`}>
                        {() =>
                          map(
                            formik.values.configs[i].customerFields,
                            (item, j) => {
                              const originalField = getFieldByName(
                                formik.values.configs[i].paymentTypeId,
                                item.name,
                              );
                              return (
                                <div key={item.name}>
                                  <HiddenField
                                    name={`configs[${i}].customerFields[${j}].hidden`}
                                    label={getFieldNameLabel(
                                      item.name,
                                      originalField,
                                    )}
                                    disabled={disabled}
                                  />

                                  {(originalField?.pattern ||
                                    originalField?.maxLength) && (
                                    <Typography
                                      sx={{ my: 2 }}
                                      color="textSecondary"
                                      variant="subtitle2"
                                    >
                                      {t('overriding.description')}
                                    </Typography>
                                  )}

                                  {originalField?.pattern && (
                                    <>
                                      <FormikTextField
                                        sx={{ my: 3 }}
                                        name={`configs[${i}].customerFields[${j}].pattern`}
                                        label={t(
                                          'overriding.fields.pattern.label',
                                        )}
                                        fullWidth
                                        disabled={disabled}
                                      />
                                      <FormikTextField
                                        name={`configs[${i}].customerFields[${j}].patternExample`}
                                        label={t(
                                          'overriding.fields.pattern_example.label',
                                        )}
                                        fullWidth
                                        disabled={disabled}
                                      />
                                    </>
                                  )}
                                  {originalField?.maxLength && (
                                    <FormikNumericField
                                      sx={{ my: 3 }}
                                      name={`configs[${i}].customerFields[${j}].maxLength`}
                                      label={t(
                                        'overriding.fields.max_length.label',
                                      )}
                                      fullWidth
                                      disabled={disabled}
                                    />
                                  )}
                                </div>
                              );
                            },
                          )
                        }
                      </FieldArray>
                    </div>
                  </FormControls>
                </Paper>
              );
            })}
            {!disabled && (
              <div>
                <Button
                  variant="outlined"
                  onClick={() => arrayHelpers.push({ paymentTypeId: '' })}
                >
                  {t('buttons.add_rule')}
                </Button>
              </div>
            )}
          </FormControls>
        )}
      />
    </Fragment>
  );
};
